import * as React from "react";
import styles from "./MSelectCard.module.css";
import * as TablerIcons from "@tabler/icons-react";

export interface MSelectCardProps extends React.HTMLAttributes<HTMLDivElement> {
  selected?: boolean;
  text: string;
  leftIcon?: string | React.ReactNode;
  rightIcon?: boolean | React.ReactNode;
  variant?: "default" | "description" | "onboarding" | "inverse";
  description?: string;
  disabled?: boolean;
  notAnswered?: boolean;
  thinBorder?: boolean;
  textVariant?: "body-base-semibold";
  centerAlign?: boolean;
}

const renderIcon = (icon?: string | React.ReactNode) => {
  if (!icon) return null;
  if (typeof icon === "string") {
    const iconName =
      "Icon" +
      icon
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join("");
    const IconComponent = (TablerIcons as any)[iconName];
    if (IconComponent) return <IconComponent size={20} />;
    return null;
  }
  // If it's a React element and supports size prop, clone with size=20
  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, { ...icon.props, size: 20 });
  }
  return icon;
};

export const MSelectCard: React.FC<MSelectCardProps> = ({
  selected = false,
  text,
  leftIcon,
  rightIcon,
  className,
  variant = "default",
  description,
  disabled = false,
  notAnswered = false,
  thinBorder = false,
  textVariant,
  centerAlign = false,
  ...props
}) => {
  return (
    <div
      className={[
        styles.selectCard,
        variant === "description" ? styles["selectCard--description"] : "",
        variant === "onboarding" ? styles["selectCard--onboarding"] : "",
        selected ? styles.selected : "",
        thinBorder ? styles.thinBorder : "",
        thinBorder && selected ? styles.thinBorderSelected : "",
        centerAlign ? styles.centerAlign : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        position: "relative",
        pointerEvents: disabled ? "none" : undefined,
        opacity: disabled ? 0.5 : undefined,
      }}
      {...props}
    >
      <div className="flex items-center gap-[var(--scale-padding-lg)] flex-1">
        {leftIcon && (
          <span
            className={[
              styles.selectCard__icon,
              variant === "onboarding"
                ? styles["selectCard__icon--onboarding"]
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={variant === "inverse" ? { color: "#45168C" } : undefined}
          >
            {renderIcon(leftIcon)}
          </span>
        )}
        {/* gap-[var(--scale-padding-xs)] reduced because the text has some padding of its own*/}
        <div className="flex flex-col flex-1 gap-[12px]">
          <span
            className={[
              styles.selectCard__text,
              variant === "description"
                ? styles["selectCard__text--description"]
                : "",
              variant === "onboarding"
                ? styles["selectCard__text--onboarding"]
                : "",
              thinBorder && selected ? styles.thinBorderTextSelected : "",
              thinBorder && !selected ? styles.thinBorderText : "",
              notAnswered ? styles.notAnswered : "",
              textVariant === "body-base-semibold" ? "body-base-semibold" : "",
              centerAlign ? styles.centerAlign : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={variant === "inverse" ? { color: "#2A2A2A" } : undefined}
          >
            {text}
          </span>
          {variant === "description" && description && (
            <span className={styles.selectCard__desc}>{description}</span>
          )}
        </div>
      </div>
      {rightIcon !== undefined && rightIcon !== false && (
        <span className={styles.selectCard__rightIcon}>{rightIcon}</span>
      )}
    </div>
  );
};

export default MSelectCard;
